import JsxHelper from '../Helpers/JsxHelper';
import React from 'react';
import EvaluatorPaymentItem from './EvaluatorPaymentItem';
import EvaluatorPaymentStatusEnum from '../Enums/EvaluatorPaymentStatusEnum';
import FieldTypesEnum from '../Enums/FieldTypesEnum';
import DataTableActionMenu from '../Elements/DataTable/DataTableActionMenu';
import AuthService from '../Services/authService';
import AssignmentPaymentDetails from '../Contents/Evaluator/EvaluatorAdmin/EvaluatorPayments/AssignmentPaymentDetails';
import EvaluatorPaymentApproveHelper from '../Helpers/ModelHelpers/EvaluatorPaymentApproveHelper';

export default class EvaluatorPaymentReviewItem extends EvaluatorPaymentItem {
  static FieldsToShowInDataTable() {
    return [
      {
        label: 'Apollo Id',
        orderById: 'AsSassieAsgnId',
        data: 'AsSassieAsgnId',
        type: FieldTypesEnum.DataFieldWithDetails,
        minWidth: 70,
      },
      {
        label: EvaluatorPaymentReviewItem.getSelectedCellHeader,
        orderById: '',
        data: EvaluatorPaymentReviewItem.getSelectedCellContent,
        type: FieldTypesEnum.PureDataField,
        minWidth: 10,
      },
      { label: 'Export', orderById: 'IeXpid', data: 'IeXpid', minWidth: 50 },
      { label: 'Type', orderById: 'IeType', data: 'IeType', minWidth: 50 },
      { label: 'Status', orderById: 'IeStatus', data: 'IeStatus', minWidth: 50 },
      // CD-5421: sits directly after Status on purpose - it is the shop-level setting behind a
      // 'Paid Outside of System' payment status, and it stays visible on payments created before it.
      {
        label: 'Paid Outside of System',
        orderById: 'PaidOutsideOfSystem',
        data: (item) => JsxHelper.YesNo(item.PaidOutsideOfSystem),
        minWidth: 20,
      },
      { label: 'Method', orderById: 'IePaymentTypeName', data: 'IePaymentTypeName', minWidth: 50 },
      { label: 'Fee', orderById: 'IeFeeDisplay', data: 'IeFeeDisplay', minWidth: 50 },
      { label: 'Fee Addit.', orderById: 'IeFeeAdjustment', data: 'IeFeeAdjustment', minWidth: 50 },
      { label: 'Expenses', orderById: 'IeExpense', data: 'IeExpense', minWidth: 50 },
      { label: 'Total', orderById: 'IeTotalDisplay', data: 'IeTotalDisplay', minWidth: 50 },
      { label: 'Tax', orderById: 'IeVat', data: 'IeVat', minWidth: 50 },
      { label: 'Paid', orderById: 'IePaidDisplay', data: 'IePaidDisplay', minWidth: 50 },
      { label: 'Due', orderById: 'IeDue', data: 'IeDue', minWidth: 50 },
      { label: 'Payment Export Date', orderById: 'IePaymentDate', data: 'IePaymentDate', minWidth: 50 },
      { label: 'Created On', orderById: 'IeCreatedOn', data: 'IeCreatedOn', minWidth: 50 },
      { label: 'Approved On', orderById: 'IeApprovedOn', data: 'IeApprovedOn', minWidth: 50 },
      {
        label: ' ',
        orderById: '',
        data: EvaluatorPaymentReviewItem.getActionCellContent,
        type: FieldTypesEnum.ActionButton,
        minWidth: 50,
      },
    ];
  }

  static getSelectedCellContent(item) {
    return (
      <input
        type='checkbox'
        checked={item.Selected}
        onChange={(e) => {
          EvaluatorPaymentReviewItem.clickOnSelect(item, e.target.checked);
        }}
      />
    );
  }

  static clickOnGlobalSelect(item, selected) {
    // Placeholder, don't remove it!
  }

  static selectedStatus() {
    return false;
  }

  static getSelectedCellHeader(item) {
    return (
      <input
        type='checkbox'
        checked={EvaluatorPaymentReviewItem.selectedStatus()}
        onChange={(e) => {
          EvaluatorPaymentReviewItem.clickOnGlobalSelect(item, e.target.checked);
        }}
      />
    );
  }

  static clickOnSelect(item, selected) {
    // Placeholder, don't remove it!
  }

  static statusChangeFunction = () => {
    // Placeholder, don't remove it!
  };

  static getActionCellContent(item) {
    // CD-5318 contract (see __tests__/Domain/EvaluatorPaymentActionGates.test.ts): action menus gate on the
    // NUMERIC status code, never on the lookup display text, so renaming a status in lookup 45 cannot
    // enable or disable an action. This grid was missed by that conversion and matched on IeStatus strings
    // until it was brought in line - keep it on IeStatusCode.
    const alwaysDisabledStatuses = [
      EvaluatorPaymentStatusEnum.Closed,
      // CD-5421: Paid Outside of System is a closed state for every gate below, exactly as Closed is -
      // it replaced the old (Closed + Assignment.PaidOutsideOfSystem) pair.
      EvaluatorPaymentStatusEnum.PaidOutsideOfSystem,
      EvaluatorPaymentStatusEnum.ToBeSentToTipalti,
      EvaluatorPaymentStatusEnum.SentToTipalti,
      EvaluatorPaymentStatusEnum.PendingTipalti,
      EvaluatorPaymentStatusEnum.DeferredOrErrorAtTipalti,
      EvaluatorPaymentStatusEnum.Void,
    ];
    const userId = AuthService.getInstance().GetLoggedInUser().userId.toString();

    const isPaymentRequestType = item.IeType === 'Payment Request';
    const isAdvancePaymentType = item.IeType === 'Advance Payment';
    const isAssignmentPaymentType = item.IeType === 'Assignment Payment';
    const isClosedStatus = [EvaluatorPaymentStatusEnum.Closed, EvaluatorPaymentStatusEnum.PaidOutsideOfSystem].includes(
      item.IeStatusCode
    );
    const isReconciledStatus = item.IeStatusCode === EvaluatorPaymentStatusEnum.Reconciled;
    const isCreatedStatus = item.IeStatusCode === EvaluatorPaymentStatusEnum.Created;
    const isApprovedStatus = item.IeStatusCode === EvaluatorPaymentStatusEnum.Approved;
    const isVoidStatus = item.IeStatusCode === EvaluatorPaymentStatusEnum.Void;

    return (
      <DataTableActionMenu
        options={[
          {
            // A Surcharge has no assignment (pAssignmentPayment_WorkflowVoidAndReissue inserts it with
            // ie_asid = null), so its Apollo Id cell is empty and the grid's usual details link has nothing
            // to open. This menu entry is the only route into a surcharge's details.
            text: `Assignment Payment (${item.IeId})`,
            link: <AssignmentPaymentDetails id={item.IeId} />,
            iconName: 'fa-solid fa-circle-info',
            hide: item.IeType !== 'Surcharge',
            url: 'paymentdetails',
            roleKey: 'EvaluatorPayment_ActionButton',
          },
          {
            text: 'Approve',
            onClick: () => EvaluatorPaymentItem.actionFunction([item.IeId], 'Approve'),
            iconName: 'fa-solid fa-check',
            // Mirrors AssignmentPaymentRepository.ValidateApprovePaymentAsync - see the twin on
            // EvaluatorPaymentItem for why the server's PendingApproval(8) clause is not mirrored.
            disabled:
              !(
                (isCreatedStatus && !isPaymentRequestType) ||
                (item.IeStatusCode === EvaluatorPaymentStatusEnum.PendingApprovalBySupervisor &&
                  isAssignmentPaymentType)
              ) || !EvaluatorPaymentApproveHelper.IsOpenToUser(item.IeStatusCode, item.IeType),
            hide: isAdvancePaymentType,
            roleKey: 'EvaluatorPayment_Approve_ActionButton',
          },
          {
            text: 'Void',
            onClick: () => EvaluatorPaymentItem.actionFunction([item.IeId], 'Void'),
            iconName: 'fa-solid fa-xmark',
            disabled: alwaysDisabledStatuses.includes(item.IeStatusCode) || isPaymentRequestType,
            roleKey: 'EvaluatorPayment_ActionButton',
          },
          {
            text: 'Void and Reissue',
            onClick: () => EvaluatorPaymentItem.actionFunction([item.IeId], 'VoidAndReissue'),
            iconName: 'fa-solid fa-rotate-right',
            disabled:
              alwaysDisabledStatuses.includes(item.IeStatusCode) ||
              [EvaluatorPaymentStatusEnum.Supressed].includes(item.IeStatusCode) ||
              isPaymentRequestType,
            roleKey: 'EvaluatorPayment_ActionButton',
          },
          {
            text: 'Void and Supress',
            onClick: () => EvaluatorPaymentItem.actionFunction([item.IeId], 'VoidAndSupress'),
            iconName: 'fa-solid fa-trash',
            disabled:
              alwaysDisabledStatuses.includes(item.IeStatusCode) ||
              [EvaluatorPaymentStatusEnum.Supressed].includes(item.IeStatusCode) ||
              isPaymentRequestType,
            roleKey: 'EvaluatorPayment_ActionButton',
          },
          {
            text: 'Reissue',
            onClick: () => EvaluatorPaymentItem.actionFunction([item.IeId], 'VoidAndReissue'),
            iconName: 'fa-solid fa-rotate-right',
            // `hide` already limits this to Void/Supressed, so the guard is just "already reissued once".
            disabled: item.IeReissuedAs !== null,
            roleKey: 'EvaluatorPayment_ActionButton',
            hide: ![EvaluatorPaymentStatusEnum.Void, EvaluatorPaymentStatusEnum.Supressed].includes(item.IeStatusCode),
          },
          {
            iconName: 'fa-solid fa-edit',
            disabled:
              (((!isPaymentRequestType && !isCreatedStatus) ||
                (isPaymentRequestType && (isClosedStatus || isReconciledStatus))) &&
                !isAdvancePaymentType &&
                (isClosedStatus || isReconciledStatus)) ||
              (isAssignmentPaymentType && !isCreatedStatus && !isApprovedStatus),
            roleKey: 'EvaluatorPayment_ActionButton',
            text: 'Edit',
            onClick: () => EvaluatorPaymentItem.editFunction(item),
          },
          {
            text: 'Status Change',
            onClick: () => EvaluatorPaymentItem.statusChangeFunction(item),
            iconName: 'fa-solid fa-rotate-right',
            disabled:
              isClosedStatus ||
              isReconciledStatus ||
              isVoidStatus ||
              (item.IeCreatedBy !== userId && item.IeAssignedTo !== userId && !AuthService.IsAdmin()),
            hide: !isPaymentRequestType && !isAdvancePaymentType,
            roleKey: 'EvaluatorPayment_ActionButton',
          },
        ]}
      />
    );
  }
}
